import role16Schema from '../../../../assets/forms/role-420000.form.json';
import { SSMT_SCOPE } from '../../../constants/qnames';

/**
 * Shared business rules for Role 16 (Statement of Comprehensive Income —
 * BEFORE TAX variant).
 *
 * Role 16 is the "before tax" alternative presentation of the Statement of
 * Comprehensive Income, parallel to role 15 (net-of-tax). Unlike role 15, it
 * ships a SINGLE schema (`role-420000.form.json`) with no MFRS/MPERS
 * variant — the schema uses `ssmt:` qnames across the board.
 *
 * Both the editor's
 * role16-420000-statement-of-comprehensive-income-before-tax.component.ts
 * AND role16SociBeforeTaxValidator import from this file so the column-suffix
 * matrix, Group/Company column-key builder, calculation row groups, and
 * LB-001 fix-guard helpers live in exactly one place.
 *
 * ---- Sheet status posture (mirror of role 11 / role 14) -----------------
 *
 * The component does NOT call
 * `stateService.updateSheetLevelStatus('statementOfComprehensiveIncomeBeforeTax', ...)`
 * anywhere. The only sheet-status write is
 * `markSheetInitializedWithStatus('statementOfComprehensiveIncomeBeforeTax',
 * false)` invoked inside `loadSchema()`'s success continuation (after the
 * calc engine's debounce settles). There is NO `evaluateSheetStatus()`
 * method, NO post-init `updateSheetLevelStatus()` call, NO
 * valueChanges-driven status push. `submitForm()` checks `form.invalid` for
 * a toast but does not propagate the result to state.
 *
 * Combined with:
 *   - the schema declaring `required: false` (implicit) on every cell and
 *     providing NO `validators` array entries on any control;
 *   - `buildForm()` creating every FormControl with an empty
 *     `validators: ValidatorFn[] = []`;
 *
 * …the editor's published `hasErrors` for this sheet is STRUCTURALLY always
 * `false` today. The pure validator mirrors this with a deterministic
 * `hasErrors: false` plus the LB-001 fix-guard (required detection from
 * BOTH `control.required === true` AND unconditional
 * `validators: [{ name: 'required' }]`) so any future schema edit gets
 * picked up automatically — matching roles 11 / 13 / 14 / 21 / 22.
 *
 * ---- Calculations (T-010 sweep — LB-015 pattern) ------------------------
 *
 * The component's `calculateTotals()` derives five totals per visible column
 * suffix:
 *
 *   ssmt:TotalItemsNotReclassifiedNetOfTax
 *     = sum(RemeasurementOfDefinedBenefitPlansBeforeTax,
 *           FairValueChangeOnEquityInvestmentsFVOCIBeforeTax,
 *           RevaluationSurplusOnPropertyPlantAndEquipmentBeforeTax,
 *           ShareOfOCIOfAssociatesNotReclassifiedBeforeTax)
 *       - TaxRelatingToItemsNotReclassified
 *
 *   ssmt:TotalItemsMayBeReclassifiedNetOfTax
 *     = sum(ExchangeDifferencesOnTranslatingForeignOperationsBeforeTax,
 *           CashFlowHedgesBeforeTax,
 *           FairValueChangeOnDebtInstrumentsFVOCIBeforeTax,
 *           ShareOfOCIOfAssociatesMayBeReclassifiedBeforeTax)
 *       - TaxRelatingToItemsMayBeReclassified
 *
 *   ssmt:OtherComprehensiveIncomeForTheYear
 *     = ssmt:TotalItemsNotReclassifiedNetOfTax
 *     + ssmt:TotalItemsMayBeReclassifiedNetOfTax
 *
 *   ssmt:TotalComprehensiveIncomeForTheYear
 *     = ssmt:ProfitForTheYearCI
 *     + ssmt:OtherComprehensiveIncomeForTheYear
 *
 *   ssmt:TotalComprehensiveIncomeAttribution
 *     = ssmt:ComprehensiveIncomeAttributableToOwnersOfParent
 *     + ssmt:ComprehensiveIncomeAttributableToNonControllingInterests
 *
 * **T-010 (LB-015 sweep) finding**: every qname the component references in
 * `calculateTotals()`, `autoFillDemo()`, and `registerCalculatedKeys()` —
 * e.g. `ssmt:RemeasurementOfDefinedBenefitPlansBeforeTax`,
 * `ssmt:TotalItemsNotReclassifiedNetOfTax`, `ssmt:ProfitForTheYearCI`,
 * `ssmt:TotalComprehensiveIncomeAttribution` — maps cleanly onto controls
 * declared in `role-420000.form.json` with the same `ssmt:` prefix. NO
 * qname divergence observed. Role 16 is confirmed CLEAN for the T-010
 * sweep (same posture as role 12 per LB-018).
 *
 * Calculated cells are persisted into the same payload via `setValue(...,
 * { emitEvent: false })`, so the pure validator reads post-calculation
 * values directly from `sheetData` — no replay required.
 *
 * ---- Cross-sheet posture -----------------------------------------------
 *
 * Role 16 is registered as Tier 4 because it sits in the same
 * dependency-graph position as role 15 (comprehensive-income cross-sheet
 * sibling). However role 16 differs from role 15 in two important ways:
 *
 *   1. The schema declares ZERO `validation.equality` blocks (role 15's
 *      cross-sheet equality against role 19 is carried in the component's
 *      `validateComprehensiveIncomeAgainstRole19()` flow, which role 16
 *      does NOT have).
 *   2. `SheetDependencyService` lists `scopeOfFiling` as an upstream of
 *      `statementOfComprehensiveIncomeBeforeTax` but does NOT wire any
 *      reverse edge to `statementOfChangesInEquity` — i.e. role 19 and
 *      role 16 are not linked.
 *
 * Net effect: role 16 has no cross-sheet equality validation today. The
 * validator still reads `context.crossSheetData?.get('scopeOfFiling')`
 * for column-visibility gating and defensively reads
 * `context.crossSheetData?.get('filingPrerequisites')` per the Tier 4
 * contract (the component's layout-change notifier watches both slices),
 * so any future schema edit adding a `validation.equality` block against
 * role 15 or role 19 plugs in without re-threading the interface.
 */

/**
 * scopeOfFiling field whose value drives Group / Company column visibility.
 * Mirrors the component's `checkConsolidatedStatus()`.
 */
export const ROLE16_CONSOLIDATED_FIELD =
  SSMT_SCOPE.DisclosureOfWhetherFinancialStatementsAreConsolidated;
export const ROLE16_CONSOLIDATED_VALUE = 'Yes';

/**
 * scopeOfFiling field whose value drives Previous-Year column visibility.
 * Mirrors the component's `checkConsolidatedStatus()`.
 */
export const ROLE16_COMPARATIVE_FIELD =
  SSMT_SCOPE.DisclosureOfComparativeInformation;
export const ROLE16_COMPARATIVE_PRESENTED_VALUES: ReadonlyArray<string> = [
  'Comparative information presented',
  'Restated comparative information',
];

/**
 * Narrow shape of a role 16 schema control used by the pure validator.
 * Keeps the type tight so unrelated schema additions don't force lockstep
 * edits here.
 */
export interface Role16SchemaValidator {
  name?: string;
  value?: number;
  conditionalOn?: unknown;
}

export interface Role16SchemaControl {
  qname: string;
  type?: string;
  required?: boolean;
  validators?: Role16SchemaValidator[];
  ui?: {
    control?: string;
    calculated?: boolean;
    readonly?: boolean;
    dualColumn?: boolean;
    level?: number;
    currency?: string;
    placeholder?: string;
    note?: string;
    calculation?: string;
  };
}

export interface Role16Schema {
  role: string;
  sheetId: string;
  label: string;
  description?: string;
  controls: Role16SchemaControl[];
}

/**
 * Resolve the role 16 schema. Single-variant — role 16 does not branch on
 * accounting standard. Exposed as a function for symmetry with role 11 /
 * role 13 so a future MFRS/MPERS split (if it ever happens) only changes
 * this one place.
 */
export function getRole16Schema(): Role16Schema {
  return role16Schema as unknown as Role16Schema;
}

/**
 * Schema controls that participate in validation. Skips section headers
 * exactly as the component's `buildForm()` does (`control.ui.control ===
 * 'header'` or `type === 'SectionHeader'`). Headers never become
 * FormControls and therefore cannot fail validation.
 */
export function getRole16ValidatingControls(
  schema: Role16Schema
): ReadonlyArray<Role16SchemaControl> {
  return schema.controls.filter((control) => !isRole16SectionHeader(control));
}

/**
 * Header detection. Mirrors the component's `isHeader()` plus the
 * `type === 'SectionHeader'` schema convention.
 */
export function isRole16SectionHeader(control: Role16SchemaControl): boolean {
  return (
    control.type === 'SectionHeader' || control.ui?.control === 'header'
  );
}

/**
 * Is this control a calculated / readonly total? Calculated cells are
 * populated by the component's `calculateTotals()` after valueChanges
 * settle. The editor never marks them required; the validator mirrors that
 * skip so even a future schema edit marking a calculated row required
 * preserves sensible behaviour.
 */
export function isRole16CalculatedControl(
  control: Role16SchemaControl
): boolean {
  return control.ui?.calculated === true || control.ui?.readonly === true;
}

/** Empty-value check matching the component's null/undefined/'' guards. */
export function isRole16Empty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * LB-001 fix-guard: a control is statically required when EITHER
 * `required === true` OR the validators array contains an unconditional
 * `{ name: 'required' }` entry. Mirrors the role-6 post-fix check so any
 * future schema edit to role 16 (none exist today) gets picked up
 * automatically without a separate component change.
 */
export function isRole16StaticallyRequired(
  control: Role16SchemaControl
): boolean {
  if (control.required === true) return true;
  return Array.isArray(control.validators)
    ? control.validators.some(
        (validator) => validator?.name === 'required' && validator?.conditionalOn === undefined
      )
    : false;
}

/**
 * Visible column suffixes for the current filing. Mirrors exactly the
 * branch ladder in the component's `buildForm()` and `calculateTotals()`.
 * Role 16 uses "Group/Company" terminology rather than role 15's
 * "Consolidated/Separate" — this matches the component's
 * `getXbrlControlName()` helper.
 *
 *   isConsolidated && hasPreviousYear  →
 *     ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious']
 *   isConsolidated && !hasPreviousYear →
 *     ['GroupCurrent', 'CompanyCurrent']
 *   !isConsolidated && hasPreviousYear →
 *     ['Current', 'Previous']
 *   !isConsolidated && !hasPreviousYear →
 *     ['Current']
 */
export type Role16ColumnSuffix =
  | 'GroupCurrent'
  | 'GroupPrevious'
  | 'CompanyCurrent'
  | 'CompanyPrevious'
  | 'Current'
  | 'Previous';

export function resolveRole16ColumnSuffixes(
  isConsolidated: boolean,
  hasPreviousYear: boolean
): ReadonlyArray<Role16ColumnSuffix> {
  if (isConsolidated && hasPreviousYear) {
    return ['GroupCurrent', 'GroupPrevious', 'CompanyCurrent', 'CompanyPrevious'];
  }
  if (isConsolidated && !hasPreviousYear) {
    return ['GroupCurrent', 'CompanyCurrent'];
  }
  if (!isConsolidated && hasPreviousYear) {
    return ['Current', 'Previous'];
  }
  return ['Current'];
}

/**
 * Resolve `(isConsolidated, hasPreviousYear)` from a `scopeOfFiling`
 * payload. Mirrors the component's `checkConsolidatedStatus()` exactly.
 *
 * When the slice is missing entirely (parity-side mid-init, or a
 * conversion the user has not opened), we default to the conservative
 * "neither flag set" — i.e. single Current column. Matches the component's
 * pre-mount initialisers (`isConsolidated = false`,
 * `hasPreviousYear = false`).
 */
export function resolveRole16LayoutFlags(
  scopeOfFilingSlice: Record<string, unknown> | undefined
): { isConsolidated: boolean; hasPreviousYear: boolean } {
  if (!scopeOfFilingSlice) {
    return { isConsolidated: false, hasPreviousYear: false };
  }
  const isConsolidated =
    scopeOfFilingSlice[ROLE16_CONSOLIDATED_FIELD] === ROLE16_CONSOLIDATED_VALUE;
  const comparative = scopeOfFilingSlice[ROLE16_COMPARATIVE_FIELD];
  const hasPreviousYear =
    typeof comparative === 'string' &&
    ROLE16_COMPARATIVE_PRESENTED_VALUES.includes(comparative);
  return { isConsolidated, hasPreviousYear };
}

/**
 * Build the flat form-control key the component's `buildForm()` uses for a
 * given row + column. Mirrors the component's key construction:
 *
 *   isConsolidated (Group/Company):
 *     `${qname}[Group|Company][CurrentYear|PreviousYear]`  (via getXbrlControlName)
 *   otherwise:
 *     `${qname}${'Current'|'Previous'}`
 *
 * The pure validator never calls `getXbrlControlName()` — it uses the
 * suffix returned by `resolveRole16ColumnSuffixes()` to pick the right
 * builder. The component's bracket-notation keys for Group/Company are
 * reproduced here so a future schema edit with dual-column required fields
 * would read the correct payload keys.
 */
export function buildRole16FlatKey(
  qname: string,
  suffix: Role16ColumnSuffix
): string {
  switch (suffix) {
    case 'GroupCurrent':
      return `${qname}[Group][CurrentYear]`;
    case 'GroupPrevious':
      return `${qname}[Group][PreviousYear]`;
    case 'CompanyCurrent':
      return `${qname}[Company][CurrentYear]`;
    case 'CompanyPrevious':
      return `${qname}[Company][PreviousYear]`;
    case 'Current':
      return `${qname}Current`;
    case 'Previous':
      return `${qname}Previous`;
  }
}
